import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography, Card, Table, Tag, Button, Modal, Alert,
  Spin, Statistic, Row, Col, message,
  Empty, Descriptions, Input
} from 'antd';
import {
  CheckCircleTwoTone, CloseCircleTwoTone, ClockCircleTwoTone,
  CreditCardTwoTone, WarningTwoTone,
  SafetyCertificateTwoTone, HistoryOutlined,
  ReloadOutlined, CheckOutlined, BankOutlined,
  UserOutlined, MailOutlined, IdcardOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Paragraph, Text } = Typography;

interface PendingPayment {
  id: string;
  invoice_id: string;
  amount: number;
  base_amount?: number;
  unique_code?: number;
  status: string;
  payment_method: string | null;
  membership_extended_days: number;
  previous_expires_at: string | null;
  new_expires_at: string | null;
  created_at: string;
  user_id: string;
  user_email: string;
  user_name: string;
  user_sub_status: string;
  user_sub_expires: string | null;
}

interface AdminPaymentsProps {
  token: string;
  API_URL: string;
  userEmail: string;
}

export const AdminPayments: React.FC<AdminPaymentsProps> = ({ token, API_URL, userEmail }) => {
  const [loading, setLoading] = useState(true);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [payments, setPayments] = useState<PendingPayment[]>([]);
  const [totalPending, setTotalPending] = useState(0);
  const [allPayments, setAllPayments] = useState<PendingPayment[]>([]);
  const [totalAll, setTotalAll] = useState(0);
  const [verifyModalVisible, setVerifyModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PendingPayment | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [searchTextPending, setSearchTextPending] = useState('');
  const [searchTextAll, setSearchTextAll] = useState('');

  const filterPayment = (payment: PendingPayment, searchText: string) => {
    if (!searchText) return true;
    const lower = searchText.toLowerCase();
    return (
      (payment.user_name || '').toLowerCase().includes(lower) ||
      (payment.user_email || '').toLowerCase().includes(lower) ||
      (payment.invoice_id || '').toLowerCase().includes(lower) ||
      String(payment.amount).includes(lower) ||
      (payment.payment_method || '').toLowerCase().includes(lower) ||
      (payment.status || '').toLowerCase().includes(lower) ||
      (payment.unique_code && String(payment.unique_code).includes(lower))
    );
  };

  const filteredPendingPayments = useMemo(() => {
    return payments.filter(p => filterPayment(p, searchTextPending));
  }, [payments, searchTextPending]);

  const filteredAllPayments = useMemo(() => {
    return allPayments.filter(p => filterPayment(p, searchTextAll));
  }, [allPayments, searchTextAll]);

  const fetchPendingPayments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/admin/payments/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPayments(data.data || []);
        setTotalPending(data.total || 0);
      }
    } catch (e) {
      console.error('Failed to fetch pending payments', e);
    }
  }, [token, API_URL]);

  const fetchAllPayments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/admin/payments/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAllPayments(data.data || []);
        setTotalAll(data.total || 0);
      }
    } catch (e) {
      console.error('Failed to fetch all payments', e);
    }
  }, [token, API_URL]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchPendingPayments(), fetchAllPayments()]);
      setLoading(false);
    };
    load();
  }, [fetchPendingPayments, fetchAllPayments]);

  const handleVerify = async () => {
    if (!selectedPayment) return;

    setVerifyingId(selectedPayment.id);
    try {
      const res = await fetch(`${API_URL}/api/v1/admin/payments/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ payment_id: selectedPayment.id })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Gagal verifikasi');

      setVerifyModalVisible(false);
      setSelectedPayment(null);
      setSuccessMsg(`✅ Membership ${data.user_email} berhasil diaktifkan hingga ${dayjs(data.new_expires_at).format('DD MMM YYYY')}!`);

      // Refresh data
      await Promise.all([fetchPendingPayments(), fetchAllPayments()]);

      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err: any) {
      message.error(err.message || 'Gagal verifikasi pembayaran');
    } finally {
      setVerifyingId(null);
    }
  };

  const renderStatus = (s: string) => {
    const map: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      paid: { color: 'green', icon: <CheckCircleTwoTone twoToneColor="#52c41a" />, label: 'Lunas' },
      pending: { color: 'gold', icon: <ClockCircleTwoTone twoToneColor="#faad14" />, label: 'Menunggu' },
      failed: { color: 'red', icon: <CloseCircleTwoTone twoToneColor="#f5222d" />, label: 'Gagal' },
      expired: { color: 'default', icon: <ClockCircleTwoTone twoToneColor="#8c8c8c" />, label: 'Kedaluwarsa' },
    };
    const m = map[s] || { color: 'default', icon: null, label: s };
    return <Tag color={m.color} style={{ fontWeight: 700 }}>{m.icon} {m.label}</Tag>;
  };

  const pendingColumns = [
    {
      title: 'User',
      dataIndex: 'user_name',
      key: 'user_name',
      sorter: (a: PendingPayment, b: PendingPayment) => (a.user_name || '').localeCompare(b.user_name || ''),
      render: (_: any, record: PendingPayment) => (
        <div>
          <div style={{ fontWeight: 800 }}>{record.user_name || '—'}</div>
          <div style={{ fontSize: '12px', color: '#868e96' }}>{record.user_email}</div>
        </div>
      ),
    },
    {
      title: 'Invoice',
      dataIndex: 'invoice_id',
      key: 'invoice_id',
      width: 180,
      render: (val: string) => (
        <Text copyable style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {val.substring(0, 24)}...
        </Text>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      sorter: (a: PendingPayment, b: PendingPayment) => a.amount - b.amount,
      render: (val: number, record: PendingPayment) => (
        <div>
          <div style={{ fontWeight: 800 }}>Rp {val.toLocaleString('id-ID')}</div>
          {record.unique_code && record.base_amount && (
            <div style={{ fontSize: '11px', color: '#868e96', marginTop: '2px' }}>
              Rp {record.base_amount.toLocaleString('id-ID')} + <span style={{ color: '#e03131', fontWeight: 700 }}>{String(record.unique_code).padStart(3, '0')}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: [
        { text: 'Lunas', value: 'paid' },
        { text: 'Menunggu', value: 'pending' },
        { text: 'Gagal', value: 'failed' },
        { text: 'Kedaluwarsa', value: 'expired' },
      ],
      onFilter: (value: boolean | React.Key, record: PendingPayment) => record.status === value,
      render: (s: string) => renderStatus(s),
    },
    {
      title: 'Konfirmasi',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 140,
      filters: [
        { text: 'Dikonfirmasi User', value: 'qris_manual' },
        { text: 'Menunggu', value: 'null' },
      ],
      onFilter: (value: boolean | React.Key, record: PendingPayment) => (value === 'null' ? !record.payment_method : record.payment_method === value),
      render: (val: string | null) => {
        if (val === 'qris_manual') {
          return <Tag color="green" style={{ fontWeight: 800, border: '2px solid #2b8a3e' }}><CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginRight: 4 }} /> Dikonfirmasi User</Tag>;
        }
        return <Tag color="default" style={{ fontWeight: 600 }}>Menunggu</Tag>;
      },
    },
    {
      title: 'Tgl. Pembayaran',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      sorter: (a: PendingPayment, b: PendingPayment) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (val: string) => (
        <span style={{ fontSize: '13px' }}>{dayjs(val).format('DD MMM YYYY HH:mm')}</span>
      ),
    },
    {
      title: 'Aksi',
      key: 'action',
      width: 100,
      render: (_: any, record: PendingPayment) => (
        <Button
          type="primary"
          size="small"
          icon={<CheckOutlined />}
          loading={verifyingId === record.id}
          onClick={() => {
            setSelectedPayment(record);
            setVerifyModalVisible(true);
          }}
          style={{
            background: '#2b8a3e',
            border: '2px solid #1a202c',
            fontWeight: 800,
            borderRadius: '6px',
            boxShadow: '1px 1px 0 #1a202c'
          }}
        >
          Verifikasi
        </Button>
      ),
    },
  ];

  const allColumns = [
    {
      title: 'User',
      dataIndex: 'user_name',
      key: 'user_name',
      sorter: (a: PendingPayment, b: PendingPayment) => (a.user_name || '').localeCompare(b.user_name || ''),
      render: (_: any, record: PendingPayment) => (
        <div>
          <div style={{ fontWeight: 800 }}>{record.user_name || '—'}</div>
          <div style={{ fontSize: '12px', color: '#868e96' }}>{record.user_email}</div>
        </div>
      ),
    },
    {
      title: 'Invoice',
      dataIndex: 'invoice_id',
      key: 'invoice_id',
      width: 160,
      render: (val: string) => (
        <Text copyable style={{ fontFamily: 'monospace', fontSize: '11px' }}>
          {val.substring(0, 20)}...
        </Text>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      sorter: (a: PendingPayment, b: PendingPayment) => a.amount - b.amount,
      render: (val: number, record: PendingPayment) => (
        <div>
          <div style={{ fontWeight: 800 }}>Rp {val.toLocaleString('id-ID')}</div>
          {record.unique_code && record.base_amount && (
            <div style={{ fontSize: '11px', color: '#868e96', marginTop: '2px' }}>
              Rp {record.base_amount.toLocaleString('id-ID')} + <span style={{ color: '#e03131', fontWeight: 700 }}>{String(record.unique_code).padStart(3, '0')}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      filters: [
        { text: 'Lunas', value: 'paid' },
        { text: 'Menunggu', value: 'pending' },
        { text: 'Gagal', value: 'failed' },
        { text: 'Kedaluwarsa', value: 'expired' },
      ],
      onFilter: (value: boolean | React.Key, record: PendingPayment) => record.status === value,
      render: (s: string) => renderStatus(s),
    },
    {
      title: 'Method',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 120,
      render: (val: string | null) => (
        <Tag style={{ fontWeight: 600 }}>{val || '—'}</Tag>
      ),
    },
    {
      title: 'Paid At',
      dataIndex: 'paid_at',
      key: 'paid_at',
      width: 140,
      render: (val: string | null) => val ? dayjs(val).format('DD MMM YYYY HH:mm') : '—',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (val: string) => dayjs(val).format('DD MMM YYYY HH:mm'),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column', gap: '16px' }}>
        <Spin size="large" />
        <span style={{ fontWeight: 800 }}>Memuat data pembayaran...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: '12px' }}>
      {/* Header */}
      <Card style={{
        border: '3px solid #1a202c',
        borderRadius: '16px',
        boxShadow: '4px 4px 0 #1a202c',
        marginBottom: '24px',
        background: 'linear-gradient(135deg, #fff9db 0%, #fff3bf 100%)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <Title level={3} style={{ margin: 0, fontWeight: 900, display: 'flex', alignItems: 'center' }}>
              <SafetyCertificateTwoTone twoToneColor="#f5222d" style={{ marginRight: 10, fontSize: '28px' }} />
              Admin — Verifikasi Pembayaran
            </Title>
            <Paragraph style={{ margin: '4px 0 0 0', color: '#495057', fontWeight: 650 }}>
              Login sebagai: <Tag color="red" style={{ fontWeight: 800 }}>{userEmail}</Tag>
            </Paragraph>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => { fetchPendingPayments(); fetchAllPayments(); }}
            style={{
              background: '#1a202c',
              color: '#fff',
              border: '2px solid #1a202c',
              fontWeight: 800,
              borderRadius: '8px'
            }}
          >
            Refresh
          </Button>
        </div>
      </Card>

      {/* Success message */}
      {successMsg && (
        <Alert
          message={successMsg}
          type="success"
          showIcon
          closable
          onClose={() => setSuccessMsg('')}
          style={{ marginBottom: '16px', border: '2px solid #2b8a3e', borderRadius: '8px' }}
        />
      )}

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={6}>
          <Card style={{ border: '2px solid #1a202c', borderRadius: '12px', boxShadow: '2px 2px 0 #1a202c' }}>
            <Statistic
              title={<span style={{ fontWeight: 800 }}><ClockCircleTwoTone twoToneColor="#faad14" style={{ marginRight: 4 }} /> Menunggu Verifikasi</span>}
              value={totalPending}
              valueStyle={{ color: '#faad14', fontWeight: 900 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card style={{ border: '2px solid #1a202c', borderRadius: '12px', boxShadow: '2px 2px 0 #1a202c' }}>
            <Statistic
              title={<span style={{ fontWeight: 800 }}><CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginRight: 4 }} /> Total Transaksi</span>}
              value={totalAll - allPayments.filter(p => p.user_email === 'a@a.com' || p.user_email.endsWith('@bazi.com')).length}
              valueStyle={{ color: '#2b8a3e', fontWeight: 900 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card style={{ border: '2px solid #1a202c', borderRadius: '12px', boxShadow: '2px 2px 0 #1a202c' }}>
            <Statistic
              title={<span style={{ fontWeight: 800 }}><CreditCardTwoTone twoToneColor="#faad14" style={{ marginRight: 4 }} /> Pending</span>}
              value={payments.reduce((s, p) => s + (p.status === 'pending' && p.user_email !== 'a@a.com' && !p.user_email.endsWith('@bazi.com') ? p.amount : 0), 0)}
              valueStyle={{ color: '#faad14', fontWeight: 900 }}
              formatter={(val) => `Rp ${Number(val).toLocaleString('id-ID')}`}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card style={{ border: '2px solid #1a202c', borderRadius: '12px', boxShadow: '2px 2px 0 #1a202c' }}>
            <Statistic
              title={<span style={{ fontWeight: 800 }}><BankOutlined style={{ marginRight: 4, color: '#2b8a3e' }} /> Total Revenue</span>}
              value={allPayments.reduce((s, p) => s + (p.status === 'paid' && p.user_email !== 'a@a.com' && !p.user_email.endsWith('@bazi.com') ? p.amount : 0), 0)}
              valueStyle={{ color: '#2b8a3e', fontWeight: 900 }}
              formatter={(val) => `Rp ${Number(val).toLocaleString('id-ID')}`}
            />
          </Card>
        </Col>
      </Row>

      {/* Pending Payments Table */}
      <Card
        style={{ border: '3px solid #1a202c', borderRadius: '16px', boxShadow: '4px 4px 0 #1a202c', marginBottom: '24px' }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ClockCircleTwoTone twoToneColor="#faad14" style={{ fontSize: '20px' }} />
            <span style={{ fontWeight: 900, fontSize: '16px' }}>Menunggu Verifikasi</span>
            <Tag color="gold" style={{ fontWeight: 800, marginLeft: 8 }}>{totalPending}</Tag>
          </div>
        }
        extra={
          <Input.Search
            placeholder="Cari (User, Invoice, dsb)..."
            allowClear
            onChange={e => setSearchTextPending(e.target.value)}
            style={{ width: 250 }}
          />
        }
      >
        {payments.length === 0 ? (
          <Empty
            description={<span style={{ fontWeight: 700 }}><CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginRight: 4 }} />Tidak ada pembayaran yang menunggu verifikasi</span>}
            style={{ padding: '40px 0' }}
          />
        ) : (
          <Table
            dataSource={filteredPendingPayments}
            columns={pendingColumns}
            rowKey="id"
            pagination={{ pageSize: 10, size: 'small' }}
            scroll={{ x: 700 }}
            style={{ fontSize: '13px' }}
          />
        )}
      </Card>

      {/* All Payments History */}
      <Card
        style={{ border: '3px solid #1a202c', borderRadius: '16px', boxShadow: '4px 4px 0 #1a202c' }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <HistoryOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
            <span style={{ fontWeight: 900, fontSize: '16px' }}>Riwayat Semua Pembayaran</span>
            <Tag color="blue" style={{ fontWeight: 800, marginLeft: 8 }}>{totalAll}</Tag>
          </div>
        }
        extra={
          <Input.Search
            placeholder="Cari (User, Invoice, dsb)..."
            allowClear
            onChange={e => setSearchTextAll(e.target.value)}
            style={{ width: 250 }}
          />
        }
      >
        <Table
          dataSource={filteredAllPayments}
          columns={allColumns}
          rowKey="id"
          pagination={{ pageSize: 10, size: 'small' }}
          scroll={{ x: 800 }}
          style={{ fontSize: '13px' }}
        />
      </Card>

      {/* Verification Confirmation Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SafetyCertificateTwoTone twoToneColor="#f5222d" style={{ fontSize: '22px' }} />
            <span style={{ fontWeight: 900 }}>Konfirmasi Verifikasi</span>
          </div>
        }
        open={verifyModalVisible}
        onCancel={() => { setVerifyModalVisible(false); setSelectedPayment(null); }}
        footer={[
          <Button key="cancel" onClick={() => { setVerifyModalVisible(false); setSelectedPayment(null); }}>
            Batal
          </Button>,
          <Button
            key="verify"
            type="primary"
            loading={verifyingId !== null}
            onClick={handleVerify}
            style={{
              background: '#2b8a3e',
              border: '2px solid #1a202c',
              color: '#fff',
              fontWeight: 900,
              borderRadius: '8px',
              boxShadow: '2px 2px 0 #1a202c'
            }}
            icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
          >
            Ya, Aktifkan Membership
          </Button>
        ]}
        width={520}
      >
        {selectedPayment && (
          <div style={{ padding: '8px 0' }}>
            <Alert
              message={<span style={{ fontWeight: 800 }}><WarningTwoTone twoToneColor="#faad14" style={{ marginRight: 4 }} /> Verifikasi Manual</span>}
              description="Pastikan kamu sudah menerima bukti pembayaran dari user sebelum mengaktifkan membership."
              type="warning"
              showIcon
              style={{ border: '2px solid #f59f00', borderRadius: '8px', marginBottom: '16px' }}
            />

            <Descriptions column={1} size="small" bordered
              style={{ backgroundColor: '#f8f9fa' }}
              labelStyle={{ fontWeight: 800, backgroundColor: '#e9ecef' }}
            >
              <Descriptions.Item label={<><UserOutlined /> Nama</>}>
                <strong>{selectedPayment.user_name || '—'}</strong>
              </Descriptions.Item>
              <Descriptions.Item label={<><MailOutlined /> Email</>}>
                {selectedPayment.user_email}
              </Descriptions.Item>
              <Descriptions.Item label={<><IdcardOutlined /> Invoice</>}>
                <Text copyable style={{ fontFamily: 'monospace' }}>{selectedPayment.invoice_id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={<><BankOutlined /> Amount</>}>
                <strong style={{ fontSize: '16px' }}>Rp {selectedPayment.amount.toLocaleString('id-ID')}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Durasi">
                {selectedPayment.membership_extended_days || 30} hari
              </Descriptions.Item>
              <Descriptions.Item label="Dibayar Pada">
                {dayjs(selectedPayment.created_at).format('DD MMM YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Status User Saat Ini">
                <Tag color={selectedPayment.user_sub_status === 'premium' ? 'gold' : 'default'}>
                  {selectedPayment.user_sub_status || 'non-premium'}
                </Tag>
              </Descriptions.Item>
              {selectedPayment.user_sub_expires && (
                <Descriptions.Item label="Expiry Saat Ini">
                  {dayjs(selectedPayment.user_sub_expires).format('DD MMM YYYY')}
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminPayments;
